// file: src/pages/pengamatan/persen-bunga/PersenBunga.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip
} from "@mui/material";
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import { ref, onValue, off } from "firebase/database";
import { database } from "../../../firebase";
import PersenBungaDialog from "./PersenBungaDialog";

const PersenBunga = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    const fetchData = () => {
      try {
        const pengamatanRef = ref(database, "db_freshpine/db_pengamatan/persen_bunga");
        
        onValue(pengamatanRef, (snapshot) => {
          const pengamatanData = snapshot.val();
          const filteredData = [];

          if (pengamatanData) {
            Object.keys(pengamatanData).forEach((key) => {
              const item = pengamatanData[key];
              
              // Cek apakah semua plot memiliki status_pengamatan_per_plot "Approved"
              const allPlotsApproved = Object.keys(item.plot || {}).every((plotKey) => {
                const plot = item.plot[plotKey];
                return plot.status_pengamatan_per_plot === "Approved";
              });

              // Cek apakah ada plot yang statusnya "Pending"
              const hasPendingPlot = Object.keys(item.plot || {}).some((plotKey) => {
                const plot = item.plot[plotKey];
                return plot.status_pengamatan_per_plot === "Pending";
              });

              // Hanya tampilkan jika semua plot approved dan tidak ada yang pending
              if (allPlotsApproved && !hasPendingPlot) {
                filteredData.push({
                  id: key,
                  id_pengamatan: item.id_pengamatan || "-",
                  tanggal_pengamatan: item.tanggal_pengamatan || "-",
                  lokasi: item.lokasi || "-",
                  nama_mandor: item.nama_mandor || "-",
                  nama_pengamat: item.nama_pengamat || "-",
                  id_lokasi: item.id_lokasi || "-",
                  plotCount: Object.keys(item.plot || {}).length,
                  originalData: item // Simpan data asli untuk detail
                });
              }
            });
          }

          // Urutkan data berdasarkan tanggal pengamatan (terbaru pertama)
          filteredData.sort((a, b) => {
            const dateA = new Date(a.tanggal_pengamatan.split('-').reverse().join('-'));
            const dateB = new Date(b.tanggal_pengamatan.split('-').reverse().join('-'));
            return dateB - dateA;
          });

          setData(filteredData);
          setLoading(false);
        }, (error) => {
          console.error("Error fetching data:", error);
          setError("Gagal memuat data persen bunga");
          setLoading(false);
        });

        return () => off(pengamatanRef);
      } catch (err) {
        console.error("Error in fetchData:", err);
        setError("Terjadi kesalahan saat memuat data");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fungsi untuk menggabungkan data berdasarkan id_lokasi
  const groupedData = data.reduce((acc, item) => {
    const existingGroup = acc.find(group => group.id_lokasi === item.id_lokasi);
    
    if (existingGroup) {
      // Jika id_lokasi sudah ada, tambahkan item ke items array
      existingGroup.items.push(item);
      // Update tanggal pengamatan terbaru
      const currentDate = new Date(item.tanggal_pengamatan.split('-').reverse().join('-'));
      const existingDate = new Date(existingGroup.tanggal_pengamatan.split('-').reverse().join('-'));
      if (currentDate > existingDate) {
        existingGroup.tanggal_pengamatan = item.tanggal_pengamatan;
      }
    } else {
      // Jika id_lokasi belum ada, buat group baru
      acc.push({
        id_lokasi: item.id_lokasi,
        lokasi: item.lokasi,
        nama_mandor: item.nama_mandor,
        tanggal_pengamatan: item.tanggal_pengamatan,
        items: [item]
      });
    }
    
    return acc;
  }, []);

  const handleGroupingData = (group) => {
    setSelectedGroup(group);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedGroup(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Persen Bunga
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Data pengamatan persen bunga dengan status Approved
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Tanggal Pengamatan</strong></TableCell>
              <TableCell><strong>Lokasi</strong></TableCell>
              <TableCell><strong>Mandor</strong></TableCell>
              <TableCell><strong>Jumlah Data</strong></TableCell>
              <TableCell><strong>Total Plot</strong></TableCell>
              <TableCell><strong>Aksi</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1" color="text.secondary" sx={{ py: 3 }}>
                    Tidak ada data persen bunga dengan status Approved
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              groupedData.map((group) => (
                <TableRow key={group.id_lokasi} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {group.tanggal_pengamatan}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {group.lokasi}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {group.id_lokasi}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {group.nama_mandor}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`${group.items.length} Data`} 
                      size="small" 
                      color="primary" 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`${group.items.reduce((total, item) => total + (item.plotCount || 0), 0)} Plot`} 
                      size="small" 
                      color="info" 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleGroupingData(group)}
                      title="Gabungkan Semua Data"
                    >
                      <MoveToInboxIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Full-screen Dialog */}
      <PersenBungaDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        groupData={selectedGroup}
        loading={false}
        error={null}
      />
    </Box>
  );
};

export default PersenBunga;